import {useTheme} from '@mui/material';
import type {DeleteRefundProcedureParams, UpdateRefundProcedureParams} from 'domain/usecases';
import type {PostRefundCreateProcedureParams} from 'domain/usecases/private/refund/request/post-create-procedure';
import {useRequestDataStore} from 'presentation/pages/private/refund/pages/Request/store/useRequestDataStore';
import {translations} from 'presentation/translations-new';
import {parseDate} from 'presentation/utils';
import {convertBRLCurrencyToNumber} from 'presentation/utils/convertBRLCurrencyToNumber';
import {useRef, useState} from 'react';
import {useFormContext, type UseFormReturn} from 'react-hook-form';
import {useTherapies} from '../../../../../hooks/useTherapies';
import {useDeleteProcedureMutation} from '../../../../../queries/useDeleteProcedureMutation';
import {useUpdateProcedureMutation} from '../../../../../queries/useUpdateProcedureMutation';
import {
	handleErrorsBuilder,
	type HandleErrorsBuilderParams,
} from '../../../../../utils/handlerErrorsBuilder';
import {procedureParamsBuilder} from '../../../../../utils/procedureParamsBuilder';
import {emptySessionBuilder} from '../../../hooks/useFormManagement';
import type {TherapyDataSchema} from '../../../schemas/therapyDataSchema';
import type {EditSessionRef} from '../../EditSession';
import type {ErrorState, TherapySessionsData} from '../types';
import type {HandleValidatorParams, UseTherapySessionsReturn} from './types';

const {
	removeModal,
	editSessionError,
	createProcedureError,
	validations: {valueGreaterThan2Cents},
} = translations['pt-br'].privateFlow.refund.therapies;

export const useTherapySessions = (): UseTherapySessionsReturn => {
	const formContext = useFormContext<TherapySessionsData>();
	const watchedSessions = formContext.watch('sessions') || [];
	const watchedTherapyType = formContext.watch('therapyType');

	const deleteProcedureMutation = useDeleteProcedureMutation();
	const updateProcedureMutation = useUpdateProcedureMutation();

	const {
		editingTherapy,
		handleCreateProcedure,
		lastSequentialProcedure,
		incrementLastSequentialProcedure,
		isLoadingProcedures,
		handleGoToTherapyMode,
		handleAddTherapy,
	} = useTherapies();
	const isEditMode = !!editingTherapy;
	const requests = useRequestDataStore(state => state.data);
	const watchedSpecialtyCode =
		requests?.requestData[0]?.treatmentData?.specialty?.specialtyCode ?? '';

	const editSessionRefs = useRef<(EditSessionRef | null)[]>([]);
	const [openRemoveModal, setOpenRemoveModal] = useState(false);
	const [errorState, setErrorState] = useState<ErrorState | null>(null);
	const [selectedSessionIndex, setSelectedSessionIndex] = useState<number | null>(null);
	const [editingSessionIndex, setEditingSessionIndex] = useState<number | null>(null);
	const [readonlySessionsCount, setReadonlySessionsCount] = useState<number>(
		watchedSessions.length,
	);
	const [sessionsWithUnsavedChanges, setSessionsWithUnsavedChanges] = useState<Set<number>>(
		new Set(),
	);
	const theme = useTheme();

	const handleAddNewSessionForm = (index: number, sequencialProcedure: number) => {
		formContext.setValue(`sessions.${index}`, emptySessionBuilder(sequencialProcedure));
	};

	const handleAddNewSessionFormAndDuplicatedValue = (
		index: number,
		sequencialProcedure: number,
		sessionValue: string,
	) => {
		formContext.setValue(`sessions.${index}`, {
			serviceDate: '',
			sessionValue,
			id: sequencialProcedure.toString(),
			isSaved: false,
		});
	};

	const currentEditableSessionIndex = watchedSessions.length - 1;
	const currentSession = watchedSessions[currentEditableSessionIndex];

	const handleError = (
		props: Omit<HandleErrorsBuilderParams, 'form'>,
		form?: UseFormReturn<TherapySessionsData>,
	) => {
		handleErrorsBuilder({
			...props,
			form: form ?? formContext,
		});
	};

	const handleValidator = ({
		currentSession,
		currentTherapyType,
		currentIndex,
		form,
	}: HandleValidatorParams) => {
		const hasServiceDate = currentSession?.serviceDate?.trim();
		const hasSessionValue = currentSession?.sessionValue?.trim();
		const sessionValueNumber = convertBRLCurrencyToNumber(hasSessionValue ?? '');
		const errorValueInvalid = hasServiceDate && hasSessionValue && sessionValueNumber <= 0.02;
		const hasTherapyType = currentTherapyType?.trim();

		if (!hasServiceDate) {
			setTimeout(() => {
				handleError({type: 'serviceDate', index: currentIndex}, form);
			}, 0);
		}

		if (!hasSessionValue) {
			setTimeout(() => {
				handleError({type: 'sessionValue', index: currentIndex}, form);
			}, 0);
		}

		if (errorValueInvalid) {
			setTimeout(() => {
				handleError(
					{
						type: 'sessionValue',
						index: currentIndex,
						message: valueGreaterThan2Cents,
					},
					form,
				);
			}, 0);
		}

		if (!hasTherapyType) {
			setTimeout(() => {
				handleError({type: 'therapyType'}, form);
			}, 0);
		}

		if (!hasServiceDate || !hasSessionValue || !hasTherapyType || errorValueInvalid) {
			return {success: false};
		}
		return {success: true};
	};

	const handleSortSessions = (sessions: TherapySessionsData['sessions']) => {
		return sessions.sort((a, b) => {
			const primaryDate = parseDate(a.serviceDate);
			const secondaryDate = parseDate(b.serviceDate);
			if (!primaryDate || !secondaryDate) {
				return 0;
			}
			return primaryDate.getTime() - secondaryDate.getTime();
		});
	};

	const handleFilterSessionsAndSort = (sessions: TherapySessionsData['sessions']) => {
		const filteredSessions = sessions.filter(session => session.isSaved);
		return handleSortSessions(filteredSessions);
	};

	const handleChangeIsSavedAndSortSessions = (sessions: TherapySessionsData['sessions']) => {
		const filteredSessions = sessions.map(session => {
			if (
				!session.isSaved &&
				session.serviceDate &&
				session.serviceDate?.trim().length > 0 &&
				session.sessionValue &&
				session.sessionValue?.trim().length > 0
			) {
				return {...session, isSaved: true};
			}
			return session;
		});
		return handleSortSessions(filteredSessions);
	};

	const addNewSession = async ({
		duplicateSessionValue,
		saveTherapyData,
	}: {
		duplicateSessionValue?: string;
		saveTherapyData?: TherapyDataSchema;
	}) => {
		try {
			const {success} = handleValidator({
				currentSession: currentSession,
				currentTherapyType: watchedTherapyType?.code,
				currentIndex: currentEditableSessionIndex,
			});

			if (!success) {
				return;
			}

			const isValidServiceDate = await formContext.trigger(
				`sessions.${currentEditableSessionIndex}.serviceDate`,
			);
			const isValidSessionValue = await formContext.trigger(
				`sessions.${currentEditableSessionIndex}.sessionValue`,
			);

			if (!isValidServiceDate || !isValidSessionValue) {
				return;
			}

			const createProcedureParams: PostRefundCreateProcedureParams = procedureParamsBuilder({
				currentProtocol: {
					protocol: requests.protocol,
					protocolCode: requests.protocolCode,
					request: requests.requestData[0].request,
					requestCode: requests.requestData[0].requestCode,
				},
				currentTherapySession: currentSession,
				procedureCode: watchedTherapyType?.code,
				specialtyCode: watchedSpecialtyCode,
			});

			const procedureCreated = await handleCreateProcedure(createProcedureParams);

			if (!procedureCreated) {
				setErrorState({
					type: 'createProcedure',
					message: createProcedureError,
				});
				return;
			}

			formContext.clearErrors('sessions');
			setReadonlySessionsCount(watchedSessions.length);
			formContext.setValue(`sessions.${currentEditableSessionIndex}`, {
				...currentSession,
				isSaved: true,
			});

			if (duplicateSessionValue) {
				handleAddNewSessionFormAndDuplicatedValue(
					currentEditableSessionIndex + 1,
					lastSequentialProcedure + 1,
					duplicateSessionValue,
				);
				incrementLastSequentialProcedure();
				return;
			}

			if (saveTherapyData) {
				const newData = {
					...saveTherapyData,
					sessions: handleChangeIsSavedAndSortSessions(saveTherapyData.sessions),
				};
				incrementLastSequentialProcedure();
				handleAddTherapy(newData);
				handleGoToTherapyMode('list');
				return;
			}

			if (!duplicateSessionValue && !saveTherapyData) {
				handleAddNewSessionForm(
					currentEditableSessionIndex + 1,
					lastSequentialProcedure + 1,
				);
				incrementLastSequentialProcedure();
			}
			return;
		} catch (error) {
			return;
		}
	};

	const handleOpenRemoveModal = (index: number) => {
		setSelectedSessionIndex(index);
		setOpenRemoveModal(true);
	};

	const handleCloseRemoveModal = () => {
		setOpenRemoveModal(false);
		setSelectedSessionIndex(null);
	};

	const removeSession = async () => {
		if (watchedSessions.length > 1 && selectedSessionIndex !== null) {
			const params: DeleteRefundProcedureParams = {
				protocolCode: requests?.protocolCode.toString() || '',
				requestCode: requests?.requestData[0].requestCode.toString() || '',
				procedureCode: watchedTherapyType?.code || '',
				sequentialProcedure: watchedSessions[selectedSessionIndex ?? 0]?.id || '',
			};
			deleteProcedureMutation.mutate(params, {
				onSuccess: () => {
					formContext.setValue(
						'sessions',
						watchedSessions.filter(
							(_, currentIndex) => currentIndex !== selectedSessionIndex,
						),
					);
					handleCloseRemoveModal();
				},
				onError: () => {
					setErrorState({
						type: 'delete',
						message: removeModal.errorMessage,
					});
					handleCloseRemoveModal();
				},
			});
		}
	};

	const duplicateSession = async (index: number) => {
		const sessionToDuplicate = watchedSessions[index];

		const hasServiceDate = currentSession?.serviceDate?.trim();
		const hasSessionValue = currentSession?.sessionValue?.trim();

		if (hasServiceDate && hasSessionValue) {
			await addNewSession({duplicateSessionValue: sessionToDuplicate.sessionValue});
			return;
		}

		formContext.setValue(`sessions.${currentEditableSessionIndex}`, {
			...currentSession,
			sessionValue: sessionToDuplicate.sessionValue,
		});
	};

	const editSession = (index: number) => {
		if (editingSessionIndex !== null) {
			editSessionRefs.current[editingSessionIndex]?.cancelEdit();
			setEditingSessionIndex(null);
		}
		if (index !== null) {
			editSessionRefs.current[index]?.editSession();
			setEditingSessionIndex(index);
		}
	};

	const cancelEditSession = (index: number) => {
		if (editingSessionIndex === null) {
			return;
		}
		editSessionRefs.current[index]?.cancelEdit();
		setEditingSessionIndex(null);
		setSessionsWithUnsavedChanges(prev => {
			const newSet = new Set(prev);
			newSet.delete(index);
			return newSet;
		});
	};

	const checkForUnsavedChanges = (): number[] => {
		const unsavedIndexes: number[] = [];
		editSessionRefs.current.forEach((sessionRef, index) => {
			if (sessionRef?.hasUnsavedChanges()) {
				unsavedIndexes.push(index);
			}
		});
		return unsavedIndexes;
	};

	const saveCurrentEditSession = async (index: number) => {
		const isSuccess = await editSessionRefs.current[index]?.saveChanges({
			isJustValidation: false,
			success: true,
		});

		if (isSuccess) {
			const currentSessionData = editSessionRefs.current[index]?.getCurrentData();

			if (!currentSessionData) {
				return;
			}

			const updateParams: UpdateRefundProcedureParams = procedureParamsBuilder({
				currentProtocol: {
					protocol: requests?.protocol || '',
					protocolCode: requests?.protocolCode || 0,
					request: requests?.requestData[0].request || '',
					requestCode: requests?.requestData[0].requestCode || 0,
				},
				currentTherapySession: currentSessionData,
				procedureCode: watchedTherapyType?.code,
				specialtyCode: watchedSpecialtyCode,
			});

			updateProcedureMutation.mutate(updateParams, {
				onSuccess: async () => {
					await editSessionRefs.current[index]?.saveChanges({
						success: true,
					});
					setEditingSessionIndex(null);
					setSessionsWithUnsavedChanges(prev => {
						const newSet = new Set(prev);
						newSet.delete(index);
						return newSet;
					});
				},
				onError: async () => {
					await editSessionRefs.current[index]?.saveChanges({
						success: false,
					});
					setErrorState({
						type: 'update',
						message: editSessionError,
					});
				},
			});
		}
	};

	const saveEditSession = async (): Promise<boolean> => {
		const unsavedIndexes = checkForUnsavedChanges();
		if (unsavedIndexes.length > 0) {
			setSessionsWithUnsavedChanges(new Set(unsavedIndexes));
			return false;
		}

		setSessionsWithUnsavedChanges(new Set());

		if (editingSessionIndex !== null) {
			const isSuccess = await editSessionRefs.current[editingSessionIndex]?.saveChanges();
			if (isSuccess) {
				setEditingSessionIndex(null);
				return true;
			}
			return false;
		}
		return true;
	};

	const handleSaveTherapy = async (data: TherapyDataSchema) => {
		const filteredSessions = data.sessions.filter(
			session =>
				session.serviceDate || (session.sessionValue && session.sessionValue !== '0,00'),
		);
		const isLastTherapyUnsaved = filteredSessions.some(
			session =>
				!session.isSaved && (session.serviceDate?.trim() || session.sessionValue?.trim()),
		);

		let dataFiltered = {...data};

		const validationPassed = await saveEditSession();

		if (!validationPassed) {
			return;
		}
		if (isLastTherapyUnsaved) {
			await addNewSession({saveTherapyData: dataFiltered});
			return;
		}

		dataFiltered = {
			...data,
			sessions: handleFilterSessionsAndSort(data.sessions),
		};
		handleAddTherapy(dataFiltered);
		handleGoToTherapyMode('list');
	};

	return {
		theme,
		isLoadingProcedures,
		isEditMode,
		openRemoveModal,
		errorState,
		readonlySessionsCount,
		sessionsWithUnsavedChanges,
		addNewSession,
		handleOpenRemoveModal,
		removeSession,
		duplicateSession,
		editSession,
		cancelEditSession,
		saveCurrentEditSession,
		saveEditSession,
		watchedSessions,
		setReadonlySessionsCount,
		formContext,
		editingSessionIndex,
		updateProcedureMutation,
		editSessionRefs,
		selectedSessionIndex,
		handleCloseRemoveModal,
		deleteProcedureMutation,
		setErrorState,
		handleSaveTherapy,
		handleAddNewSessionForm,
		lastSequentialProcedure,
		handleValidator,
	};
};
